import React, { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { BottomSheet, useOptionalBottomSheet } from '../BottomSheet'

// Shell for the multi-step booking forms: ServicesModal on /home and
// Services on the public pages. Title, step progress and close button on top,
// one scroll area below; the steps render their own NavigationButtons
// (components/commons/BookingFormComponents.js), which stick to the bottom of
// that scroll area.
//
// Popup (default): a bottom sheet on phones, a centered dialog on wider
// screens. Dragging and backdrop taps do not close it, so a stray swipe or tap
// cannot throw away a half-filled form; the close button does.
//
// inline: the same header and body as a card in the page flow, for pages that
// show the form in place (the personal trainer hero, /services/[countryCd]).

// Same height budget as the aircon picker sheet, so moving from a picker into
// the form does not make the sheet jump.
const SHEET_MAX_HEIGHT = "92vh"

// "Step 2 of 3 · Date & time" and a segmented bar. Renders nothing when
// stepIndex is outside steps (e.g. a service list or success screen).
export const StepProgress = ({ steps = [], stepIndex = -1 }) => {
    if (stepIndex < 0 || stepIndex >= steps.length) return null
    return (
        <>
            <p className="text-sm text-gray-500 mt-0.5">
                Step {stepIndex + 1} of {steps.length} · {steps[stepIndex]}
            </p>
            <div className="mt-2 flex gap-1.5" aria-hidden="true">
                {steps.map((step, i) =>
                    <div key={step} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= stepIndex ? 'bg-orange-500' : 'bg-gray-200'}`} />
                )}
            </div>
        </>
    )
}

const BookingHeader = ({ title, steps, stepIndex, onClose }) => {
    // Inside the sheet, close through it so it slides away first; the sheet
    // then calls onClose.
    const sheet = useOptionalBottomSheet()
    const close = sheet ? sheet.onClose : onClose
    return (
        <div className="shrink-0 flex items-start justify-between gap-3 px-4 sm:px-6 py-2.5 border-b border-gray-100">
            <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{title}</h2>
                <StepProgress steps={steps} stepIndex={stepIndex} />
            </div>
            {onClose &&
                <button
                    type="button"
                    onClick={close}
                    className="p-2 -mr-2 hover:bg-gray-100 rounded-full transition-colors shrink-0"
                    aria-label="Close"
                >
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            }
        </div>
    )
}

export const BookingSheet = ({
    title,
    steps,
    stepIndex = -1,
    // Omit (or pass showClose={false}) to hide the close button
    onClose,
    showClose = true,
    inline = false,
    // Scrolls the body back to the top when it changes (pass the current step)
    scrollKey,
    maxWidth = "2xl",
    children,
}) => {
    const bodyRef = useRef(null)
    useEffect(() => {
        bodyRef.current?.scrollTo?.({ top: 0 })
    }, [scrollKey])

    const header = (
        <BookingHeader
            title={title}
            steps={steps}
            stepIndex={stepIndex}
            onClose={showClose ? onClose : undefined}
        />
    )

    if (inline) {
        return (
            // The inline form is always on the page, so it only holds off the
            // auto-reload (VersionWatcher) once the customer has moved past step 1.
            <div data-no-auto-reload={stepIndex > 0 ? "" : undefined} className="max-w-2xl mx-auto w-full rounded-3xl shadow-xl bg-white overflow-hidden flex flex-col max-h-[95vh]">
                {header}
                <div ref={bodyRef} id="Services" className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide">
                    <div className="px-4 py-4">
                        {children}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <BottomSheet
            isOpen={true}
            onClose={onClose}
            maxWidth={maxWidth}
            maxHeight={SHEET_MAX_HEIGHT}
            disableDrag
            showDragHandle={false}
            closeOnBackdrop={false}
        >
            {/* Own flex column instead of BottomSheet.Content (which reserves a
                fixed header + footer height). dvh where supported, so the
                on-screen keyboard does not push the buttons out of view. */}
            <div className="flex flex-col max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh]">
                {header}
                <div ref={bodyRef} id="Services" className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-auto">
                    <div className="px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                        {children}
                    </div>
                </div>
            </div>
        </BottomSheet>
    )
}
